import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { syncFoundingMemberFeedback } from '@/lib/founding-member-sync';

export type FeedbackType = 'bug' | 'feature' | 'question';
export type FeedbackStatus = 'new' | 'reviewed' | 'in_progress' | 'completed' | 'declined';
export type BugSeverity = 'blocking' | 'annoying' | 'minor';

interface FeedbackContext {
  current_path: string;
  journey_progress: number | null;
  browser: string;
  viewport: string;
  submitted_at: string;
}

interface Feedback {
  id: string;
  user_id: string;
  type: FeedbackType;
  title: string;
  description: string | null;
  status: string;
  severity: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: any;
  github_issue_number: number | null;
  github_issue_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeedbackWithUser extends Feedback {
  userEmail?: string;
  userName?: string;
}

function parseBrowserInfo(userAgent: string): string {
  let browser = 'Unknown';
  let os = 'Unknown';

  // Detect browser
  if (userAgent.includes('Firefox/')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Edg/')) {
    browser = 'Edge';
  } else if (userAgent.includes('Chrome/')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
  } else if (userAgent.includes('Opera') || userAgent.includes('OPR/')) {
    browser = 'Opera';
  }

  // Detect OS
  if (userAgent.includes('Windows')) {
    os = 'Windows';
  } else if (userAgent.includes('Mac OS')) {
    os = 'macOS';
  } else if (userAgent.includes('Linux')) {
    os = 'Linux';
  } else if (userAgent.includes('Android')) {
    os = 'Android';
  } else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    os = 'iOS';
  }

  return `${browser} on ${os}`;
}

function captureContext(journeyProgress: number | null): FeedbackContext {
  return {
    current_path: window.location.pathname,
    journey_progress: journeyProgress,
    browser: parseBrowserInfo(navigator.userAgent),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    submitted_at: new Date().toISOString(),
  };
}

export function useFeedback() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [myFeedback, setMyFeedback] = useState<Feedback[]>([]);
  const [allFeedback, setAllFeedback] = useState<FeedbackWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const submitFeedback = async (
    type: FeedbackType, 
    title: string, 
    description?: string,
    severity?: BugSeverity
  ) => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user profile for name and journey progress
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, first_name, last_name, onboarding_step')
        .eq('user_id', user.id)
        .maybeSingle();

      // Calculate journey progress (simplified - based on onboarding step)
      const journeyProgress = profile?.onboarding_step 
        ? Math.min(Math.round((profile.onboarding_step / 5) * 100), 100)
        : null;

      // Capture context
      const context = captureContext(journeyProgress);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const insertData: any = {
        user_id: user.id,
        type,
        title,
        description: description || null,
        severity: type === 'bug' ? (severity || null) : null,
        context,
      };

      const { data: feedback, error } = await supabase
        .from('feedback')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Send email notification
      try {
        const userName = profile?.display_name ||
          [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
          undefined;

        await supabase.functions.invoke('send-feedback-notification', {
          body: {
            feedbackId: feedback.id,
            type,
            title,
            description,
            severity: type === 'bug' ? severity : undefined,
            context,
            userEmail: user.email,
            userName,
          },
        });
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
        // Don't fail the whole operation if email fails
      }

      // Sync founding member feedback count (fire-and-forget)
      syncFoundingMemberFeedback(user.id);

      toast({
        title: type === 'bug' 
          ? 'Bug report submitted' 
          : type === 'question'
          ? 'Question submitted'
          : 'Feature request submitted',
        description: 'Thank you for your feedback!',
      });

      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit feedback. Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchMyFeedback = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMyFeedback((data as Feedback[]) || []);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllFeedback = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Get user profiles for display names
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(f => f.user_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name, first_name, last_name')
          .in('user_id', userIds);

        const profileMap = new Map(
          profiles?.map(p => [p.user_id, p]) || []
        );

        const feedbackWithUsers = data.map(f => {
          const profile = profileMap.get(f.user_id);
          return {
            ...f,
            type: f.type as FeedbackType,
            userName: profile?.display_name || 
              [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || 
              undefined,
          };
        });

        setAllFeedback(feedbackWithUsers);
      } else {
        setAllFeedback([]);
      }
    } catch (error) {
      console.error('Error fetching all feedback:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateFeedbackStatus = async (feedbackId: string, status: FeedbackStatus) => {
    try {
      const { error } = await supabase
        .from('feedback')
        .update({ status })
        .eq('id', feedbackId);

      if (error) throw error;

      setAllFeedback(prev => 
        prev.map(f => f.id === feedbackId ? { ...f, status } : f)
      );

      toast({
        title: 'Status updated',
        description: `Feedback status changed to ${status.replace('_', ' ')}`,
      });

      return true;
    } catch (error) {
      console.error('Error updating feedback status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status.',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    submitFeedback,
    isSubmitting,
    fetchMyFeedback,
    myFeedback,
    fetchAllFeedback,
    allFeedback,
    updateFeedbackStatus,
    isLoading,
  };
}
